'use client';

import { cn } from '@/lib/utils';
import { VA_EVENTS } from '@/lib/analytics/event-catalog';
import { vaTrack } from '@/lib/analytics/vercel-events';
import type { LegalTocEntry } from '@/lib/legal-toc';
import { useReducedMotion } from '@/lib/use-reduced-motion';

function scrollToHeading(id: string, reducedMotion: boolean) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({
    behavior: reducedMotion ? 'auto' : 'smooth',
    block: 'start',
  });
}

export function LegalTableOfContents({
  entries,
  className,
  showHeading = true,
}: {
  entries: LegalTocEntry[];
  className?: string;
  /** Hide the “On this page” label when wrapped (e.g. mobile `<details>` with its own summary). */
  showHeading?: boolean;
}) {
  const reducedMotion = useReducedMotion();

  if (!entries.length) return null;

  return (
    <nav aria-label="On this page" className={cn(className)}>
      {showHeading ? (
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          On this page
        </p>
      ) : null}
      <ul className="space-y-1 border-l border-border">
        {entries.map((entry) => (
          <li key={entry.id}>
            <a
              href={`#${entry.id}`}
              onClick={(e) => {
                e.preventDefault();
                vaTrack(VA_EVENTS.LEGAL_TOC_CLICK, { heading_id: entry.id });
                scrollToHeading(entry.id, reducedMotion);
                history.replaceState(null, '', `#${entry.id}`);
              }}
              style={{ paddingLeft: entry.depth * 12 + 12 }}
              className={cn(
                'block py-1 text-sm leading-snug text-muted-foreground border-l border-transparent',
                'hover:text-foreground hover:border-foreground/25 transition-colors',
                entry.depth > 0 && 'font-normal',
              )}
            >
              <span className="line-clamp-3">{entry.title}</span>
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
